import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, CardBody, Col, Input, InputGroup, InputGroupText, Progress, Row, Table } from 'reactstrap';
import { FaBarcode, FaSearch, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { productActions } from '../../sagas/productSlice';
import { getWooCommerceProductSynchronization, synchronizeWooCommerceProduct } from '../../services/woocommerceConfigurationService';

const WooCommerceProductsPage = () => {
  const dispatch = useDispatch();
  const searchRef = useRef(null);
  const { products = [], loading } = useSelector(state => state.products);
  const [query, setQuery] = useState('');
  const [statuses, setStatuses] = useState({});
  const [syncingId, setSyncingId] = useState(null);

  const loadProducts = value => dispatch(productActions.findAllArticles({
    query: value.trim(),
    page: 0,
    size: 100
  }));

  useEffect(() => {
    searchRef.current?.focus();
    loadProducts('');
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => loadProducts(query), 350);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let active = true;
    Promise.all(products.map(async product => {
      try {
        return [product.id, (await getWooCommerceProductSynchronization(product.id)).data];
      } catch (_) {
        return [product.id, null];
      }
    })).then(values => active && setStatuses(Object.fromEntries(values)));
    return () => { active = false; };
  }, [products]);

  const handleSearchKeyDown = event => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    loadProducts(query);
  };

  const clearSearch = () => {
    setQuery('');
    loadProducts('');
    searchRef.current?.focus();
  };

  const synchronize = async product => {
    setSyncingId(product.id);
    try {
      const { data } = await synchronizeWooCommerceProduct(product.id);
      setStatuses(previous => ({ ...previous, [product.id]: data }));
      if (data.status === 'FAILED') throw new Error(data.error || 'Synchronisation échouée.');
      await Swal.fire('Succès', 'Produit synchronisé avec WooCommerce.', 'success');
      loadProducts(query);
    } catch (error) {
      await Swal.fire('Erreur', error.response?.data?.message || error.message || 'Synchronisation impossible.', 'error');
    } finally {
      setSyncingId(null);
      searchRef.current?.focus();
    }
  };

  return <Card><CardBody>
    <Row className="align-items-center mb-3">
      <Col md={6}>
        <h4 className="mb-1">Synchronisation des produits</h4>
        <p className="text-muted mb-0">Les articles créés ou modifiés récemment apparaissent en premier.</p>
      </Col>
      <Col md={6}>
        <InputGroup>
          <InputGroupText><FaSearch /></InputGroupText>
          <Input
            innerRef={searchRef}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Scanner un code-barres ou rechercher par libellé…"
            aria-label="Rechercher un produit"
          />
          {query && <Button color="light" onClick={clearSearch} title="Effacer"><FaTimes /></Button>}
          <InputGroupText title="Compatible avec une douchette code-barres"><FaBarcode /></InputGroupText>
        </InputGroup>
        <div className="small text-muted mt-1">Scannez le code-barres ou appuyez sur Entrée pour lancer immédiatement la recherche.</div>
      </Col>
    </Row>

    <Table responsive hover>
      <thead><tr><th>Produit</th><th>Code-barres</th><th>Statut WooCommerce</th><th>Progression</th><th /></tr></thead>
      <tbody>
        {!loading && products.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-4">Aucun produit trouvé.</td></tr>}
        {products.map(product => {
          const status = statuses[product.id];
          const busy = syncingId === product.id;
          const complete = status?.status && status.status !== 'FAILED';
          return <tr key={product.id}>
            <td>{product.libelle}</td>
            <td>{product.codeBarre || '—'}</td>
            <td><Badge color={status?.status === 'FAILED' ? 'danger' : complete ? 'success' : 'secondary'}>{status?.status || 'NOT_SYNCED'}</Badge>{status?.error && <div className="small text-danger">{status.error}</div>}</td>
            <td style={{ minWidth: 150 }}><Progress value={busy ? 50 : complete ? 100 : 0}>{busy ? 'En cours' : complete ? '100%' : '0%'}</Progress></td>
            <td><Button color="primary" size="sm" disabled={syncingId !== null || loading} onClick={() => synchronize(product)}>Synchroniser</Button></td>
          </tr>;
        })}
      </tbody>
    </Table>
  </CardBody></Card>;
};

export default WooCommerceProductsPage;
